/*
 * Copyright (c) 2024. Amazon.com, Inc. or its affiliates. All rights reserved.
 */
import { jsx } from '@amazon/vinyl-tsx'
import { el } from '@amazon/vinyl-tsx'
import {
    isPromiseLike,
    isSilentError,
    logError,
    type LogTarget,
    type MaybePromise,
} from '@amazon/vinyl-util'
import { asData, type ObservableValue } from '@amazon/vinyl-observable'
import type { I18nString } from '@amzn/bf-i18n'
import { Icon } from '@/components/Icon'
import { claimErrorSurface } from '@/errorHandler'

// Can be approximate, will remove and GC the child after the transition
const FADE_OUT_DURATION = 1

const target: LogTarget = { logPrefix: 'toast' }

/**
 * A localized message accepted by {@link toast}. Requiring the branded
 * {@link I18nString} (never a plain `string`) is what forces every toasted
 * message to have come through the i18n layer. A `MaybePromise` is accepted so
 * a caller can hand `getStringAsync(...)` directly — it is awaited before the
 * toast renders. An `ObservableValue` keeps updating the rendered text if the
 * bundle resolves later; an `HTMLElement` mounts as a custom-composed body.
 */
type ToastMessage = MaybePromise<I18nString> | ObservableValue<I18nString> | HTMLElement

/**
 * Show a transient toast message.
 *
 * The message must be localized: pass an {@link I18nString} (from
 * `getString(...).value`), an `ObservableValue<I18nString>`
 * (a bare `getString(...)`), or a `Promise<I18nString>` (`getStringAsync(...)`).
 * A plain `string` is rejected at compile time.
 */
export function toast(message: ToastMessage, opts: { time?: number } = {}) {
    const { time = 3 } = opts
    if (isPromiseLike(message)) {
        Promise.resolve(message)
            .then((resolved) => showMessageToast(resolved, time))
            .catch((e) => logError(target, 'toast message failed to resolve', e))
        return
    }
    showMessageToast(message, time)
}

function showMessageToast(
    message: I18nString | ObservableValue<I18nString> | HTMLElement,
    time: number
) {
    // Element inputs (e.g. a custom-composed toast body) mount as-is;
    // strings and observables render inside a shared `.message` wrapper.
    // asData wraps a plain string; observables pass through untouched
    // so vinyl-tsx re-renders when the i18n bundle resolves.
    const div =
        message instanceof HTMLElement ? message : <div className="message">{asData(message)}</div>
    showToast(div, time)
}

/**
 * A message accepted by {@link toastError}. Like {@link ToastMessage} the
 * string-message path is localization-gated (a plain `string` is rejected), but
 * a raw `Error` is still accepted — that path derives its own message, so the
 * error handler's debug sink and `.catch(...)` callers can pass errors through.
 */
type ToastErrorMessage = MaybePromise<I18nString> | ObservableValue<I18nString> | Error

function messageToObservable(
    message: I18nString | Error | ObservableValue<I18nString>
): ObservableValue<string> {
    if (typeof message === 'string') return asData(message)
    if (message instanceof Error) return asData(message.message)
    // ObservableValue<I18nString> widens to ObservableValue<string> (covariant).
    return message
}

export function toastError(
    message: ToastErrorMessage,
    opts: { time?: number; dedupe?: boolean } = {}
) {
    const { time = 5, dedupe = true } = opts
    // Silent errors (e.g. AbortError thrown by abort/withAbort, or
    // any error tagged via createSilentError) signal "we don't want
    // user-facing chrome for this". Honor that contract here so
    // callers don't each have to filter manually.
    if (isSilentError(message)) return
    if (isPromiseLike(message)) {
        Promise.resolve(message)
            .then((resolved) => showErrorToast(resolved, time, dedupe))
            .catch((e) => logError(target, 'toast error message failed to resolve', e))
        return
    }
    showErrorToast(message, time, dedupe)
}

function showErrorToast(
    message: I18nString | Error | ObservableValue<I18nString>,
    time: number,
    dedupe: boolean
) {
    // Normalize to an observable so the rendered text auto-updates
    // when a passed `getString(...)` bundle resolves.
    const observable = messageToObservable(message)
    const text = observable.value
    if (dedupe && !claimErrorSurface(message, text)) return

    const div = (
        <div className="message error" role="alert">
            <Icon icon="exclamationFilled" className="errorIcon" />
            <span className="errorText">{observable}</span>
        </div>
    )
    showToast(div, time)
}

function showToast(div: HTMLElement, time: number) {
    // `.messages` is a persistent `aria-live="polite"` region (see AppView):
    // it stays in the a11y tree even when empty, so appending a node into an
    // already-live region announces reliably. A container that is display:none
    // until a toast fires would be absent from the a11y tree on append and
    // announce nothing for a polite (non-alert) toast.
    const messages = el('#toastContainer > .messages')
    messages.appendChild(div)

    setTimeout(() => {
        div.classList.add('hide')
        setTimeout(() => {
            messages.removeChild(div)
        }, FADE_OUT_DURATION * 1000)
    }, time * 1000)
}
